import React, { useEffect, useRef, useState } from 'react';
import { hasGoogleMapsKey, loadGoogleMaps } from './googleMapsLoader';
import { getLocationAddress, normalizeLocationValue } from './locationUtils';
import './LocationInput.css';

const mapPlaceToLocation = (place) => {
  if (!place) return null;
  const location = place.geometry?.location;
  const lat = typeof location?.lat === 'function' ? location.lat() : null;
  const lng = typeof location?.lng === 'function' ? location.lng() : null;
  return normalizeLocationValue({
    address: place.formatted_address || place.name || '',
    formatted_address: place.formatted_address || '',
    place_id: place.place_id || '',
    lat,
    lng,
  });
};

const LocationInput = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Gateadresse, postnummer og sted',
  'aria-label': ariaLabel,
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [autocompleteReady, setAutocompleteReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const address = getLocationAddress(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;
    const keepTypingInsideInput = (event) => {
      if (event.key === ' ' || event.key === 'Spacebar') {
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        event.stopPropagation();
      }
    };
    input.addEventListener('keydown', keepTypingInsideInput, true);
    return () => {
      input.removeEventListener('keydown', keepTypingInsideInput, true);
    };
  }, []);

  useEffect(() => {
    if (disabled || !hasGoogleMapsKey() || !inputRef.current) return undefined;
    let cancelled = false;
    let listener = null;

    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current) return;
        const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'geometry.location', 'name', 'place_id'],
          types: ['address'],
          componentRestrictions: { country: ['no'] },
        });
        listener = autocomplete.addListener('place_changed', () => {
          const selected = mapPlaceToLocation(autocomplete.getPlace());
          if (selected) onChangeRef.current(selected);
        });
        autocompleteRef.current = autocomplete;
        setAutocompleteReady(true);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });

    return () => {
      cancelled = true;
      if (listener?.remove) listener.remove();
      autocompleteRef.current = null;
    };
  }, [disabled]);

  return (
    <span className="location-input-shell">
      <input
        ref={inputRef}
        className="input"
        value={address}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="street-address"
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Spacebar') {
            event.stopPropagation();
          }
        }}
        onChange={(event) => onChange(normalizeLocationValue(event.target.value))}
      />
      {hasGoogleMapsKey() && !loadFailed ? (
        <span className={`location-input-status ${autocompleteReady ? 'is-ready' : ''}`} aria-hidden="true">
          {autocompleteReady ? 'Maps' : '...'}
        </span>
      ) : null}
    </span>
  );
};

export default LocationInput;
